import supabase from '@/supabaseClient'
import { Game } from '@/types/database'
import { GameProcessingResult } from './types'

export class GenreService {
  static async processGameGenres(
    gameData: GameProcessingResult,
    game: Game
  ): Promise<boolean> {
    if (!gameData.genres || gameData.genres.length === 0) {
      return false
    }

    try {
      // Fetch existing genres
      const genreNames = gameData.genres.map((g) => g.name)
      const { data: existingGenres, error: genresError } = await supabase
        .from('genres')
        .select('id, name')
        .in('name', genreNames)

      if (genresError) {
        console.error('Error fetching existing genres:', genresError)
        return false
      }

      // Insert new genres
      const existingNames = new Set(existingGenres?.map((g) => g.name) || [])
      const newGenres = gameData.genres
        .filter((g) => !existingNames.has(g.name))
        .map((g) => ({ name: g.name }))

      if (newGenres.length > 0) {
        const { error: insertGenresError } = await supabase
          .from('genres')
          .insert(newGenres)

        if (insertGenresError) {
          console.error('Error inserting new genres:', insertGenresError)
          return false
        }
      }

      // Get all genre IDs for associations
      const { data: allGenres, error: allGenresError } = await supabase
        .from('genres')
        .select('id, name')
        .in('name', genreNames)

      if (allGenresError) {
        console.error('Error fetching all genres:', allGenresError)
        return false
      }

      return await this.createGameGenreAssociations(game.id, allGenres || [], game.title)
    } catch (error) {
      console.error('Error processing game genres:', error)
      return false
    }
  }

  private static async createGameGenreAssociations(
    gameId: string,
    allGenres: { id: string; name: string }[],
    gameTitle: string
  ): Promise<boolean> {
    // Check existing associations
    const { data: existingGameGenres, error: checkGameGenresError } = await supabase
      .from('game_genres')
      .select('game_id, genre_id')
      .eq('game_id', gameId)

    if (checkGameGenresError) {
      console.error('Error checking existing game_genres:', checkGameGenresError)
      return false
    }

    // Create new associations
    const existingGenreIds = new Set(
      (existingGameGenres || []).map((gg) => gg.genre_id)
    )

    const gameGenres = allGenres
      .filter((genre) => !existingGenreIds.has(genre.id))
      .map((genre) => ({
        game_id: gameId,
        genre_id: genre.id,
        created_at: new Date().toISOString(),
      }))

    if (gameGenres.length > 0) {
      const { error: gameGenresError } = await supabase
        .from('game_genres')
        .upsert(gameGenres, { onConflict: 'game_id,genre_id' })

      if (gameGenresError) {
        console.error('Error inserting game_genres:', gameGenresError)
        return false
      }

      console.log(`Successfully added ${gameGenres.length} genres for ${gameTitle}`)
    } else {
      console.log(`No new genres to add for ${gameTitle}`)
    }

    return true
  }
}